import { useQuery } from '@tanstack/react-query'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { QueryKeys } from '~/services/queryClient'
import { SocketEvent } from '~/services/socket'
import store, { RootState } from '~/store'
import { setIsAgentUnavailable } from '~/store/appSlice'
import { fetchWrapper } from '~/utils'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BetterToast, ToastIds } from '~/utils/toast'
import { useUserContext } from './UserContext'

export interface AppConfiguration {
  email_verification_enabled: boolean
  phone_verification_enabled: boolean
  mulesoft_to_nodejs_enabled: boolean
  mulesoft_to_camel_enabled: boolean
  // Single kill switch for the Spring Integration refactor agent. Backend
  // config controls whether the command card is rendered + the refactor
  // pipeline is reachable; when false, the SI agent (the trigger, the
  // completeness gate, all parallelism, the curated examples, the GitHub
  // fetcher) is effectively disabled because users can't initiate it.
  // No backend env vars needed — gating the trigger gates the feature.
  spring_integration_refactoring_enabled: boolean
  sso_enabled: boolean
  sso_type: string
  agent_type: number
  plugin_enabled: boolean
}

interface AppConfigContextValue {
  config: AppConfiguration
  loading: boolean
  error: string | null
  refetch: () => void
}

const defaultConfig: AppConfiguration = {
  email_verification_enabled: false,
  phone_verification_enabled: false,
  mulesoft_to_nodejs_enabled: false,
  mulesoft_to_camel_enabled: false,
  // Default false matches the other feature-gated commands here — the SI
  // agent is opt-in until the backend `/api/config/app` response sets it
  // to true. Production-rollback is a one-line config flip on the API,
  // not a frontend deploy.
  spring_integration_refactoring_enabled: false,
  sso_enabled: false,
  sso_type: '',
  agent_type: 0,
  plugin_enabled: false
}

const AppConfigContext = createContext<AppConfigContextValue | null>(null)

interface AppConfigProviderProps {
  children: React.ReactNode
}

export function AppConfigProvider({ children }: AppConfigProviderProps) {
  const [config, setConfig] = useState<AppConfiguration>(defaultConfig)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetchWrapper.get<AppConfiguration>('/api/config/app')
      setConfig(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration')
      console.error('Failed to load app configuration:', err)
      // Keep default config on error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  // Listen for agent status updates via WebSocket
  useEffect(() => {
    const onSocketEvent = (data: { data: { online: boolean }; event: string }) => {
      if (data.event !== SocketEvent.AgentStatusUpdated) return
      store.dispatch(setIsAgentUnavailable(!data.data.online))
    }
    globalEventEmitter.on(AppEvents.SocketEvent, onSocketEvent)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, onSocketEvent)
    }
  }, [])

  // Listen for reload events via WebSocket
  useEffect(() => {
    const onReloadEvent = (data: { event: string; data: { reason?: string } }) => {
      if (data.event !== SocketEvent.Reload) return

      // Handle reload based on data context
      const reloadReason = data.data?.reason

      // Only reload for legitimate reasons (e.g., trial expiry, server restart)
      const legitimateReasons = ['trial_expired', 'server_restart', 'force_update']

      if (reloadReason && legitimateReasons.includes(reloadReason)) {
        console.log('Reloading page due to:', reloadReason)
        setTimeout(() => {
          location.reload()
        }, 1000 * 3)
      } else {
        // For authentication issues, redirect to signin instead of reload
        if (reloadReason === 'auth_required' || reloadReason === 'session_expired') {
          window.location.href = '/signin'
        }
      }
    }

    globalEventEmitter.on(AppEvents.SocketEvent, onReloadEvent)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, onReloadEvent)
    }
  }, [])

  const contextValue: AppConfigContextValue = useMemo(
    () => ({
      config,
      loading,
      error,
      refetch: fetchConfig
    }),
    [config, loading, error, fetchConfig]
  )

  return <AppConfigContext.Provider value={contextValue}>{children}</AppConfigContext.Provider>
}

export function useAppConfig(): AppConfigContextValue {
  const context = useContext(AppConfigContext)
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider')
  }
  return context
}

// Individual config hooks for convenience
export function useConfigValue<K extends keyof AppConfiguration>(key: K): AppConfiguration[K] {
  const { config } = useAppConfig()
  return config[key]
}

// User-specific configuration interface
export interface UserSpecificConfiguration {
  is_super_admin: boolean
}

// Hook to get user-specific configuration (requires authentication).
// Backed by React Query so every consumer shares one cache entry and we
// avoid refetching `/api/config/app/user` on every WorkspaceHomePage mount.
// `is_super_admin` doesn't change mid-session, so once cached we don't
// invalidate — page refresh picks up changes.
//
// Persistence is opted out (`meta.persist = false`): `is_super_admin` is
// per-user and could survive into another user's session on a shared
// machine if persisted to localStorage.
export function useUserSpecificConfig() {
  const { isAuthenticated, user } = useUserContext()
  const query = useQuery({
    queryKey: QueryKeys.UserAppConfig,
    queryFn: () => fetchWrapper.get<UserSpecificConfiguration>('/api/config/app/user'),
    // This endpoint 403s for an unverified user (EmailVerificationMiddleware).
    // ManageAccountDialog — which calls this hook — is mounted unconditionally
    // by ModalProvider for every authenticated user, so without this gate an
    // unverified signup fires 3 requests (retry: 2 below) that each 403 and
    // spam a duplicate toast.
    enabled: isAuthenticated && user?.email_verified === true,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Recover from a transient first-load failure so consumers don't
    // permanently silently fall back to non-admin rendering.
    retry: 2,
    meta: { persist: false }
  })

  return {
    userConfig: query.data ?? null,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch
  }
}

// Per-user feature flags returned by ``GET /api/config/features``.
//
// Backend convention: each flag is computed server-side as
// ``user.email in <FEATURE>_USERS ∪ ALL_ACCESS_USERS``. The endpoint
// is auth-required (the global ``/api/config/app`` is not — it cannot
// see the user, so per-user gates live here). When a flag isn't in the
// response (older clients hitting newer servers, or vice-versa), treat
// it as ``false``.
export interface FeatureFlags {
  agentic_ux?: boolean
  mule_version_upgrade_users?: boolean
  virus_scan_enabled?: boolean
  // Per-user override for the Spring Integration refactor tile. The
  // global ``spring_integration_refactoring_enabled`` from
  // ``AppConfiguration`` is the deployment-wide kill switch; this flag
  // additionally unlocks individual users (or anyone in
  // ``ALL_ACCESS_USERS``) regardless of the global default. The
  // frontend ORs the two — either gate opens the feature.
  spring_integration_refactoring?: boolean
}

// React-Query backed feature-flag fetcher. Mirrors the shape of
// ``useUserSpecificConfig`` above so consumers can switch between
// them without learning a new pattern. Persistence is off for the
// same reason: flags are per-user and must not leak across sessions
// on a shared machine.
export function useFeatureFlags() {
  const query = useQuery({
    queryKey: QueryKeys.FeatureFlags,
    queryFn: () => fetchWrapper.get<FeatureFlags>('/api/config/features'),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
    meta: { persist: false }
  })

  return {
    flags: query.data ?? null,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch
  }
}

// Hook to check if agent is unavailable
export function useAgentUnavailable() {
  return useSelector((state: RootState) => state.app.isAgentUnavailable)
}

// Hook to show toast when agent is unavailable (must be used within a component)
export function useAgentUnavailableToast() {
  const isAgentUnavailable = useAgentUnavailable()
  useEffect(() => {
    if (isAgentUnavailable) {
      BetterToast.stickyError({
        id: ToastIds.AgentUnavailable,
        message: 'Service Unavailable',
        loading: true
      })
    } else {
      BetterToast.dismiss(ToastIds.AgentUnavailable)
    }
  }, [isAgentUnavailable])
}
